import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Chip, Alert, CircularProgress, TextField, Grid, LinearProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
} from '@mui/material';

import { loginAPI } from '../services/api';

const NATIVE = { select: { native: true }, inputLabel: { shrink: true } };
const errText = (err, fallback) => err.response?.data?.detail || fallback;
const fmtTime = (d) => (d ? new Date(d).toLocaleString() : '-');

// "Chrome on Windows" from a user-agent string; the full text is kept in the tooltip
export function browserOf(ua) {
  if (!ua) return '-';
  const browser = /Edg\//.test(ua) ? 'Edge' : /OPR\//.test(ua) ? 'Opera' : /Firefox\//.test(ua) ? 'Firefox'
    : /Chrome\//.test(ua) ? 'Chrome' : /Safari\//.test(ua) ? 'Safari' : 'Other';
  const os = /Windows/.test(ua) ? 'Windows' : /iPhone|iPad/.test(ua) ? 'iOS' : /Android/.test(ua) ? 'Android'
    : /Mac OS X/.test(ua) ? 'macOS' : /Linux/.test(ua) ? 'Linux' : '';
  return os ? `${browser} on ${os}` : browser;
}

const OUTCOME = {
  login: { label: 'Signed in', color: 'success' },
  denied: { label: 'Refused', color: 'warning' },
  failed: { label: 'Failed', color: 'error' },
};

function Stat({ label, value }) {
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="h5">{value ?? '-'}</Typography>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
    </Paper>
  );
}

function LoginHistory() {
  const [rows, setRows] = useState(null);
  const [summary, setSummary] = useState(null);
  const [outcome, setOutcome] = useState('');
  const [days, setDays] = useState('30');
  const [search, setSearch] = useState('');
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await loginAPI.events({ outcome: outcome || undefined, q: search.trim() || undefined, days: Number(days) });
      setRows(res.data);
      setError(null);
    } catch (err) { setError(errText(err, 'Could not load the login history')); setRows([]); }
  }, [outcome, days, search]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  useEffect(() => { loginAPI.summary().then((r) => setSummary(r.data)).catch(() => {}); }, []);

  return (
    <Box>
      <Typography variant="h4" gutterBottom>Login History</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Every sign-in to the portal, including refused users and failed one-time codes. Records cannot be changed or deleted.
        {summary?.recording_since && ` Recording since ${fmtTime(summary.recording_since)}.`}
      </Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid size={{ xs: 6, md: 3 }}><Stat label="Sign-ins, last 24 hours" value={summary?.logins_24h} /></Grid>
        <Grid size={{ xs: 6, md: 3 }}><Stat label="Sign-ins, last 7 days" value={summary?.logins_7d} /></Grid>
        <Grid size={{ xs: 6, md: 3 }}><Stat label="People, last 7 days" value={summary?.users_7d} /></Grid>
        <Grid size={{ xs: 6, md: 3 }}><Stat label="Refused or failed, last 7 days" value={summary?.problems_7d} /></Grid>
      </Grid>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField select size="small" label="Result" value={outcome} onChange={(e) => setOutcome(e.target.value)} slotProps={NATIVE} sx={{ minWidth: 160 }}>
            <option value="">All</option>
            <option value="login">Signed in</option>
            <option value="denied">Refused</option>
            <option value="failed">Failed</option>
          </TextField>
          <TextField select size="small" label="Period" value={days} onChange={(e) => setDays(e.target.value)} slotProps={NATIVE} sx={{ minWidth: 160 }}>
            <option value="1">Last 24 hours</option>
            <option value="7">Last 7 days</option>
            <option value="30">Last 30 days</option>
            <option value="90">Last 90 days</option>
            <option value="0">All time</option>
          </TextField>
          <TextField size="small" label="Email or address" value={search} onChange={(e) => setSearch(e.target.value)} sx={{ minWidth: 260 }} slotProps={{ inputLabel: { shrink: true } }} />
        </Box>
      </Paper>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow><TableCell>When</TableCell><TableCell>User</TableCell><TableCell>Result</TableCell><TableCell>Method</TableCell>
              <TableCell>Address</TableCell><TableCell>Browser</TableCell><TableCell>Detail</TableCell></TableRow>
          </TableHead>
          <TableBody>
            {(rows || []).map((r) => (
              <TableRow key={r.id} hover>
                <TableCell>{fmtTime(r.at)}</TableCell>
                <TableCell>{r.email}</TableCell>
                <TableCell><Chip size="small" color={OUTCOME[r.outcome]?.color || 'default'} label={OUTCOME[r.outcome]?.label || r.outcome} /></TableCell>
                <TableCell>{r.method === 'entra' ? 'Microsoft' : 'One-time code'}</TableCell>
                <TableCell>{r.ip_address || '-'}</TableCell>
                <TableCell title={r.user_agent || ''}>{browserOf(r.user_agent)}</TableCell>
                <TableCell>{r.detail || '-'}</TableCell>
              </TableRow>
            ))}
            {rows && rows.length === 0 && <TableRow><TableCell colSpan={7} align="center">No sign-ins recorded for these filters</TableCell></TableRow>}
          </TableBody>
        </Table>
        {rows === null && <Box sx={{ p: 2 }}><CircularProgress size={24} /></Box>}
        {rows && rows.length >= 500 && <Alert severity="info" sx={{ m: 1 }}>Showing the latest 500. Narrow the period or search to see others.</Alert>}
      </TableContainer>
      {rows === null && <LinearProgress />}
    </Box>
  );
}

export default LoginHistory;
